import { emptyEox7Api } from "src/common/service/eox7/emptyEox7Api";
import { companyDomainPath, documentPath } from "./paths";
import type {
	DocumentTokenArgs,
	DomainDocumentItem,
	DomainDocumentItemWire,
	DomainMyDocuments,
	DomainMyDocumentsWire,
	OpenTokenResponse,
	ViewTokenResponse,
} from "./types";

/** The listing walks every document in the domain, so it can take well past the default. */
const DOCUMENT_LIST_TIMEOUT_MS = 15_000;
/** Inside the browser's ~5 s user-activation window, so a slow mint ends in the error toast, not a silently refused Excel launch. */
const TOKEN_MINT_TIMEOUT_MS = 3_000;

const DOCUMENT_TYPES: ReadonlySet<string> = new Set<DomainDocumentItem["type"]>(
	["file", "folder"],
);

const isDocumentType = (type: string): type is DomainDocumentItem["type"] =>
	DOCUMENT_TYPES.has(type);

/** Drops items of a type the portal doesn't know rather than guessing how to show them. */
const normalizeDocumentItems = (
	items: DomainDocumentItemWire[],
): DomainDocumentItem[] =>
	items.flatMap((item) => {
		const type = item.type.toLowerCase();
		return isDocumentType(type) ? [{ ...item, type }] : [];
	});

export const excelReportsApi = emptyEox7Api.injectEndpoints({
	endpoints: (builder) => ({
		/**
		 * The first call provisions a report user for the caller in the domain;
		 * a 403 means they can't be one there, so render it as no access, not an error.
		 */
		getMyDocuments: builder.query<DomainMyDocuments, string>({
			query: (companyDomainId) => ({
				url: `${companyDomainPath(companyDomainId)}/documents/me`,
				timeout: DOCUMENT_LIST_TIMEOUT_MS,
			}),
			transformResponse: (response: DomainMyDocumentsWire) => ({
				...response,
				documents: normalizeDocumentItems(response.documents),
				favorites: normalizeDocumentItems(response.favorites),
			}),
			providesTags: (_result, _error, companyDomainId) => [
				{ type: "ExcelReports", id: companyDomainId },
			],
		}),
		createOpenToken: builder.mutation<OpenTokenResponse, DocumentTokenArgs>({
			query: (args) => ({
				url: `${documentPath(args)}/open-token`,
				method: "POST",
				// Injects the Store add-in; the token is bound to these values.
				params: { injectAddin: "prod", platform: "desktop" },
				timeout: TOKEN_MINT_TIMEOUT_MS,
			}),
		}),
		createViewToken: builder.mutation<ViewTokenResponse, DocumentTokenArgs>({
			query: (args) => ({
				url: `${documentPath(args)}/view-token`,
				method: "POST",
				timeout: TOKEN_MINT_TIMEOUT_MS,
			}),
		}),
	}),
});

export const {
	useGetMyDocumentsQuery,
	useCreateOpenTokenMutation,
	useCreateViewTokenMutation,
} = excelReportsApi;
